import Header from "../header/header";
import { Box, Container, Divider, Typography } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useAPI } from "../../api/useAPI";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DBAccount } from "../../api/types";
import ExpandableGrid from "../../components/ExpandableGrid";
import AccountOverview from "./components/acountOverview";
import SimulatedPositionsContextProvider from "../../context/simulatedPositionsContext";
import PositionsContextProvider from "../../context/positionsContext";
import { SettingsModal } from "../settings/SettingsModal";
import { AccountTileTitle } from "./components/account-tile-title";
import { useRefetchLoop } from "../../hooks/useRefetchLoop";
import DisguiseContextProvider, {
  useDisguise,
} from "../../context/disguiseContext";
import { DashboardFooter } from "./components/DashboardFooter";
import { categorizeAccounts, knownOwners } from "./accountCategories";

export default function DashboardScreen() {
  return (
    <DisguiseContextProvider>
      <DashboardContent />
    </DisguiseContextProvider>
  );
}

function DashboardContent() {
  const { getAccounts } = useAPI();
  const { enabled: disguise } = useDisguise();
  const [accounts, setAccounts] = useState<DBAccount[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadAccounts = useCallback(() => {
    getAccounts().then((accounts) => {
      if (accounts.success) {
        const visible = accounts.data.filter(
          (account) => account.archived !== true,
        );
        setAccounts(visible);
      }
    });
  }, [getAccounts, setAccounts]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useRefetchLoop(loadAccounts, 60);


  const categories = useMemo(
    () => categorizeAccounts(accounts),
    [accounts],
  );

  const owners = useMemo(() => knownOwners(accounts), [accounts]);

  const renderAccount = (account: DBAccount) => (
    <ExpandableGrid
      key={account._id.toString()}
      defaultOpen={false}
      content={(isOpen) => (
        <AccountTileTitle isOpen={isOpen} account={account} />
      )}
    >
      <PositionsContextProvider>
        <AccountOverview
          account={account}
          reloadAccounts={loadAccounts}
          owners={owners}
        />
      </PositionsContextProvider>

      <Box sx={{ mt: 4 }}>
        <Divider />
      </Box>
    </ExpandableGrid>
  );

  return (
    <SimulatedPositionsContextProvider>
      <Container maxWidth="xl">
        <Header />
        <SettingsModal
          handleClose={() => setSettingsOpen(false)}
          open={settingsOpen}
        />

        {categories.personal.length > 0 && (
          <>
            <SectionHeader title="Personal" />
            {categories.personal.map((group) => (
              <Box key={group.owner} sx={{ mb: 2 }}>
                <OwnerHeader
                  owner={group.owner}
                  count={group.accounts.length}
                  totalEquity={group.totalEquity}
                />
                <Box sx={{ pl: 2 }}>{group.accounts.map(renderAccount)}</Box>
              </Box>
            ))}
          </>
        )}

        {categories.testing.length > 0 && (
          <>
            <SectionHeader title="Testing" />
            {categories.testing.map(renderAccount)}
          </>
        )}

        {categories.disabled.length > 0 && (
          <ExpandableGrid
            defaultOpen={false}
            content={(isOpen) => (
              <SectionHeader
                title={`Disabled (${categories.disabled.length})`}
                isOpen={isOpen}
              />
            )}
          >
            {categories.disabled.map(renderAccount)}
          </ExpandableGrid>
        )}

        <DashboardFooter />
      </Container>
    </SimulatedPositionsContextProvider>
  );
}

function SectionHeader({ title, isOpen }: { title: string; isOpen?: boolean }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mt: 3,
        mb: 1,
        borderBottom: 1,
        borderColor: "divider",
        width: "100%",
      }}
    >
      <Typography
        variant="overline"
        color="textSecondary"
        sx={{ fontSize: "0.9rem", letterSpacing: 2 }}
      >
        {title}
      </Typography>
      {isOpen !== undefined && (
        <Box sx={{ ml: "auto", display: "flex" }}>
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </Box>
      )}
    </Box>
  );
}

function OwnerHeader({
  owner,
  count,
  totalEquity,
}: {
  owner: string;
  count: number;
  totalEquity: number;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mt: 1 }}>
      <Typography variant="h6" color="primary">
        {owner}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        ${Math.round(totalEquity)} &middot; {count}{" "}
        {count === 1 ? "account" : "accounts"}
      </Typography>
    </Box>
  );
}
